import { useState } from 'react';
import {
  Box, Drawer, Tabs, Tab, useTheme, useMediaQuery,
  Typography, IconButton, Fab,
} from '@mui/material';
import HeadsetMicIcon from '@mui/icons-material/HeadsetMic';
import { useLocation } from 'react-router-dom';
import { NewTicketForm } from './NewTicketForm';
import { TicketList } from './TicketList';
import { TicketDetail } from './TicketDetail';
import { useSupport } from '../../hooks/useSupport';

function resolveModuleFromPath(pathname: string): string | undefined {
  if (pathname.includes('/fees')) return 'Fees';
  if (pathname.includes('/attendance')) return 'Attendance';
  if (pathname.includes('/students')) return 'Students';
  if (pathname.includes('/employees')) return 'Employees';
  if (pathname.includes('/timetable')) return 'Timetable';
  if (pathname.includes('/exams')) return 'Exams';
  if (pathname.includes('/announcements')) return 'Announcements';
  if (pathname.includes('/hostel')) return 'Hostel';
  if (pathname.includes('/transport')) return 'Transport';
  if (pathname.includes('/reports')) return 'Reports';
  if (pathname.includes('/settings')) return 'Settings';
  return undefined;
}

export function SupportWidget() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const location = useLocation();
  const { useMyTickets } = useSupport();
  const { data: ticketsData } = useMyTickets({ pageSize: 50 });

  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState(0);
  const [selectedTicketId, setSelectedTicketId] = useState<number | null>(null);

  const currentModule = resolveModuleFromPath(location.pathname);
  const openCount = ticketsData?.data?.filter(
    (t) => t.status === 'Open' || t.status === 'InProgress' || t.status === 'Reopened'
  ).length ?? 0;

  const handleTicketCreated = () => setTab(1);
  const handleSelectTicket = (id: number) => setSelectedTicketId(id);
  const handleBackToList = () => setSelectedTicketId(null);

  const drawerContent = (
    <Box sx={{
      width: isMobile ? '100vw' : 420,
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      bgcolor: '#fff',
    }}>
      {/* Header */}
      <Box sx={{
        px: 2.5, pt: 2.5, pb: 0, flexShrink: 0,
        borderBottom: '1px solid #f0f0f0',
        background: 'linear-gradient(135deg, #faf8ff 0%, #f5f3ff 100%)',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1.25 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.375 }}>
              <Box sx={{
                width: 7, height: 7, borderRadius: '50%', bgcolor: '#22c55e',
                animation: 'sLive 2s ease-out infinite',
                '@keyframes sLive': {
                  '0%': { boxShadow: '0 0 0 0 rgba(34,197,94,0.5)' },
                  '70%': { boxShadow: '0 0 0 5px rgba(34,197,94,0)' },
                  '100%': { boxShadow: '0 0 0 0 rgba(34,197,94,0)' },
                },
              }} />
              <Typography sx={{ fontSize: '0.62rem', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: '#16a34a' }}>
                Support Online
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1.05rem', color: '#1a1b23', lineHeight: 1.2 }}>
              Help &amp; Support
            </Typography>
            {currentModule && (
              <Typography sx={{ fontSize: '0.68rem', color: '#9ca3af', mt: 0.25 }}>
                · {currentModule}
              </Typography>
            )}
          </Box>
          <IconButton
            size="small"
            onClick={() => setOpen(false)}
            sx={{
              color: '#9ca3af', bgcolor: '#f9fafb', border: '1px solid #e5e7eb',
              width: 30, height: 30,
              '&:hover': { color: '#374151', bgcolor: '#f3f4f6' },
            }}
          >
            <svg width="11" height="11" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <path d="M1 1l10 10M11 1L1 11" />
            </svg>
          </IconButton>
        </Box>

        {!selectedTicketId && (
          <Tabs
            value={tab}
            onChange={(_, v: number) => setTab(v)}
            sx={{
              minHeight: 36,
              '& .MuiTabs-indicator': { height: 2, background: '#8A70D6', borderRadius: '2px 2px 0 0' },
              '& .MuiTab-root': {
                minHeight: 36, fontSize: '0.75rem', fontWeight: 600,
                color: '#9ca3af', textTransform: 'none',
                px: 0, mr: 2.5, minWidth: 'auto',
                '&.Mui-selected': { color: '#8A70D6' },
              },
            }}
          >
            <Tab label="New Request" />
            <Tab label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                My Tickets
                {openCount > 0 && (
                  <Box sx={{
                    px: 0.625, height: 15, borderRadius: '8px',
                    bgcolor: '#ede9fe', color: '#8A70D6',
                    fontSize: '0.57rem', fontWeight: 800,
                    display: 'flex', alignItems: 'center',
                    border: '1px solid #ddd6fe',
                  }}>
                    {openCount}
                  </Box>
                )}
              </Box>
            } />
          </Tabs>
        )}
      </Box>

      {/* Body */}
      <Box sx={{
        flex: 1, overflowY: 'auto', px: 2.5, pt: 2, pb: 2,
        scrollbarWidth: 'thin', scrollbarColor: '#e5e7eb transparent',
        '&::-webkit-scrollbar': { width: 4 },
        '&::-webkit-scrollbar-thumb': { background: '#e5e7eb', borderRadius: 4 },
      }}>
        {selectedTicketId
          ? <TicketDetail ticketId={selectedTicketId} onBack={handleBackToList} />
          : tab === 0
            ? <NewTicketForm onSuccess={handleTicketCreated} currentModule={currentModule} />
            : <TicketList onSelect={handleSelectTicket} />
        }
      </Box>
    </Box>
  );

  return (
    <>
      <Fab
        color="primary"
        size="medium"
        onClick={() => setOpen(true)}
        aria-label="Help & Support"
        sx={{
          position: 'fixed', bottom: 24, right: 24,
          zIndex: theme.zIndex.fab,
          boxShadow: '0 4px 16px rgba(138,112,214,0.35)',
          '&:hover': { boxShadow: '0 6px 24px rgba(138,112,214,0.45)' },
        }}
      >
        {openCount > 0 ? (
          <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <HeadsetMicIcon />
            <Box sx={{
              position: 'absolute', top: -10, right: -10,
              width: 16, height: 16, borderRadius: '50%',
              bgcolor: '#f59e0b', border: '2px solid #fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '0.55rem', fontWeight: 800, color: '#fff',
            }}>
              {openCount > 9 ? '9+' : openCount}
            </Box>
          </Box>
        ) : (
          <HeadsetMicIcon />
        )}
      </Fab>

      <Drawer
        anchor={isMobile ? 'bottom' : 'right'}
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{
          sx: {
            height: isMobile ? '92vh' : '100%',
            borderRadius: isMobile ? '20px 20px 0 0' : 0,
            boxShadow: '-4px 0 24px rgba(0,0,0,0.1)',
            overflow: 'hidden',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    </>
  );
}
